import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import DataTable, { TableColumn } from 'react-data-table-component';
import {
  Button,
  Card,
  CardBody,
  Col,
  Form,
  Input,
  Label,
  Row,
  Spinner,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { H3 } from '../../../AbstractElements';
import DeleteConfirm from '../../../Components/Modals/Delete';
import Loader from '../../../Components/Widgets/Loader/generic';
import { get, postJson } from '../../../hook/request';

type Festivita = {
  ID: number;
  data: string;
  nome: string;
};

function extractFestivita(value: any): Festivita[] {
  const root = value?.data ?? value;
  const list = Array.isArray(root)
    ? root
    : Array.isArray(root?.data)
      ? root.data
      : [];

  return list.map((item: any) => ({
    ID: Number(item.ID ?? item.id),
    data: String(item.giorno ?? ''),
    nome: String(item.titolo ?? ''),
  }));
}

function formatDate(value?: string) {
  if (!value) return '-';
  const date = value.split('T')[0].split(' ')[0];
  const parts = date.split('-');
  if (parts.length !== 3) return value;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

export default function GestioneFestivita() {
  const [rows, setRows] = useState<Festivita[]>([]);
  const [data, setData] = useState('');
  const [nome, setNome] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadFestivita = async () => {
    try {
      setLoading(true);
      const response = await get<Festivita[] | { data?: Festivita[] }>(
        '/presenze/listFestivita'
      );
      setRows(extractFestivita(response));
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ??
          error?.message ??
          'Errore nel caricamento delle festività'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFestivita();
  }, []);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!data || !nome.trim()) {
      toast.warn('Inserisci data e nome della festività');
      return;
    }

    try {
      setSaving(true);
      await postJson('/presenze/addFestivita', {
        data,
        nome: nome.trim(),
      });
      toast.success('Festività aggiunta con successo');
      setData('');
      setNome('');
      await loadFestivita();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ??
          error?.message ??
          'Errore durante l’aggiunta della festività'
      );
    } finally {
      setSaving(false);
    }
  };

  const columns: TableColumn<Festivita>[] = useMemo(
    () => [
      {
        name: 'Data',
        selector: row => row.data,
        sortable: true,
        width: '180px',
        cell: row => formatDate(row.data),
      },
      {
        name: 'Nome',
        selector: row => row.nome,
        sortable: true,
        grow: 2,
      },
      {
        name: 'Azioni',
        width: '140px',
        button: true,
        ignoreRowClick: true,
        cell: row => (
          <DeleteConfirm
            url={`/presenze/festivita?ID=${encodeURIComponent(
              String(row.ID)
            )}`}
            title="Elimina festività"
            message={`Eliminare la festività "${row.nome}"?`}
            onSuccess={loadFestivita}
            trigger={
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
              >
                Elimina
              </button>
            }
          />
        ),
      },
    ],
    []
  );

  return (
    <div className="page-body pt-4">
      <Card>
        <CardBody>
          <H3 className="mb-4">Gestione festività</H3>

          <Form onSubmit={handleSubmit} className="mb-4">
            <Row className="g-3 align-items-end">
              <Col md={3}>
                <Label for="festivita-data">Data</Label>
                <Input
                  id="festivita-data"
                  type="date"
                  value={data}
                  onChange={event => setData(event.target.value)}
                  disabled={saving}
                />
              </Col>

              <Col md={6}>
                <Label for="festivita-nome">Nome</Label>
                <Input
                  id="festivita-nome"
                  type="text"
                  placeholder="Es. Festa della Repubblica"
                  value={nome}
                  onChange={event => setNome(event.target.value)}
                  disabled={saving}
                />
              </Col>

              <Col md={3}>
                <Button color="primary" type="submit" disabled={saving}>
                  {saving ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Aggiunta...
                    </>
                  ) : (
                    'Aggiungi'
                  )}
                </Button>
              </Col>
            </Row>
          </Form>

          <div className="table-responsive">
            <DataTable
              columns={columns}
              data={rows}
              progressPending={loading}
              progressComponent={<Loader />}
              striped
              pagination
              highlightOnHover
              dense
              noDataComponent="Nessuna festività disponibile"
              paginationPerPage={25}
              customStyles={{
                rows: {
                  style: {
                    minHeight: '42px',
                  },
                },
              }}
            />
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
